// frontend/src/contexts/AuthContext.jsx
// ============================================================================
// CONTEXT D'AUTHENTIFICATION MULTI-TENANT
// VERSION CORRIGÉE : Sessions séparées + vérification expiration JWT + anti-boucle PWA
// ============================================================================
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/apiBase';

// ============================================================================
// CONFIGURATION API
// ============================================================================
// ============================================================================
// ✅ FIX — Décoder et vérifier l'expiration d'un JWT côté client
// ============================================================================
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp && payload.exp < now) {
      console.log('🔐 [Auth] Token expiré depuis', new Date(payload.exp * 1000).toLocaleString());
      return true;
    }
    return false;
  } catch (e) {
    console.warn('⚠️ [Auth] Token malformé');
    return true;
  }
};

// ============================================================================
// HELPERS POUR GESTION DES CLÉS LOCALSTORAGE
// ============================================================================

const getStoragePrefix = (role) => {
  return role === 'super_admin' ? 'superadmin_' : '';
};

const storeAuthData = (token, user, company) => {
  const prefix = getStoragePrefix(user.role);
  localStorage.setItem(`${prefix}token`, token);
  localStorage.setItem(`${prefix}user`, JSON.stringify(user));
  if (company) {
    localStorage.setItem(`${prefix}company`, JSON.stringify(company));
  }
  console.log(`💾 [Auth] Données stockées avec préfixe: "${prefix}"`);
};

const getAuthData = () => {
  const superAdminToken = localStorage.getItem('superadmin_token');
  const normalToken = localStorage.getItem('token');

  // Conflit : deux sessions → tout nettoyer
  if (superAdminToken && normalToken) {
    console.error('🚨 [Auth] CONFLIT : 2 sessions détectées ! Nettoyage...');
    localStorage.clear();
    return null;
  }

  // Session super-admin
  if (superAdminToken) {
    // ✅ FIX — Vérifier expiration avant de restaurer
    if (isTokenExpired(superAdminToken)) {
      console.log('🔐 [Auth] Token super-admin expiré, nettoyage...');
      localStorage.removeItem('superadmin_token');
      localStorage.removeItem('superadmin_user');
      localStorage.removeItem('superadmin_company');
      return null;
    }

    const user = localStorage.getItem('superadmin_user');
    const company = localStorage.getItem('superadmin_company');

    if (!user) {
      localStorage.removeItem('superadmin_token');
      localStorage.removeItem('superadmin_company');
      return null;
    }

    console.log('🔵 [Auth] Session super-admin restaurée');
    return {
      token: superAdminToken,
      user: JSON.parse(user),
      company: company ? JSON.parse(company) : null,
      prefix: 'superadmin_'
    };
  }

  // Session utilisateur normal
  if (normalToken) {
    // ✅ FIX — Vérifier expiration avant de restaurer
    if (isTokenExpired(normalToken)) {
      console.log('🔐 [Auth] Token entreprise expiré, nettoyage...');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('company');
      // Ne pas supprimer hp_parametres_pointage ni hp_kiosk_load_date
      return null;
    }

    const user = localStorage.getItem('user');
    const company = localStorage.getItem('company');

    if (!user) {
      localStorage.removeItem('token');
      localStorage.removeItem('company');
      return null;
    }

    console.log('🟢 [Auth] Session entreprise restaurée');
    return {
      token: normalToken,
      user: JSON.parse(user),
      company: company ? JSON.parse(company) : null,
      prefix: ''
    };
  }

  console.log('⚪ [Auth] Aucune session');
  return null;
};

const clearAuthData = (role) => {
  const prefix = getStoragePrefix(role);
  localStorage.removeItem(`${prefix}token`);
  localStorage.removeItem(`${prefix}user`);
  localStorage.removeItem(`${prefix}company`);
  console.log(`🗑️ [Auth] Données nettoyées avec préfixe: "${prefix}"`);
};

// ============================================================================
// CRÉATION DU CONTEXTE
// ============================================================================
const AuthContext = createContext(null);

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ==========================================================================
  // VÉRIFIER LE TOKEN AU CHARGEMENT DE L'APP
  // ==========================================================================
  useEffect(() => {
    const initAuth = async () => {
      try {
        const authData = getAuthData();

        if (authData) {
          setToken(authData.token);
          setUser(authData.user);
          setCompany(authData.company);
          axios.defaults.headers.common['Authorization'] = `Bearer ${authData.token}`;
          console.log(`✅ [Auth] Session restaurée (${authData.prefix ? 'super-admin' : 'entreprise'})`);
        }
        // Si authData est null (token expiré), loading passe à false → PrivateRoute
        // redirige vers /login proprement, sans boucle
      } catch (err) {
        console.error('❌ [Auth] Erreur restauration session:', err);
        localStorage.clear();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // ==========================================================================
  // ✅ FIX — Vérification périodique de l'expiration du token (toutes les 5 min)
  // Évite que la tablette reste connectée avec un token mort sans s'en apercevoir
  // ==========================================================================
  useEffect(() => {
    if (!token) return;

    const checkExpiry = setInterval(() => {
      if (isTokenExpired(token)) {
        console.log('🔐 [Auth] Token expiré détecté en cours de session, déconnexion...');
        logout();
      }
    }, 5 * 60 * 1000); // toutes les 5 minutes

    return () => clearInterval(checkExpiry);
  }, [token]);

  // ==========================================================================
  // LOGIN
  // ==========================================================================
  const login = async (email, password, totpCode = '') => {
    try {
      setError(null);
      setLoading(true);

      console.log('🔐 [Auth] Tentative de connexion:', email);

      const payload = { email, password };
      if (totpCode) {
        payload.totpCode = totpCode;
      }

      const response = await axios.post(`${API_URL}/api/auth/login`, payload);

      if (response.data?.requiresTotp) {
        return {
          success: false,
          requiresTotp: true,
          setupRequired: response.data.setupRequired,
          totpSetup: response.data.totpSetup || null,
          message: response.data.message || 'Code Google Authenticator requis.',
          user: response.data.user || null
        };
      }

      const { token: newToken, user: userData } = response.data;

      if (!newToken || !userData) {
        throw new Error('Données de connexion invalides');
      }

      console.log('🧹 [Auth] Nettoyage sessions existantes...');

      if (userData.role === 'super_admin') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('company');
      } else {
        localStorage.removeItem('superadmin_token');
        localStorage.removeItem('superadmin_user');
        localStorage.removeItem('superadmin_company');
      }

      const companyData = userData.role === 'super_admin' ? null : {
        id: userData.company_id,
        name: userData.company_name || 'Entreprise',
        slug: userData.company_slug || '',
        status: 'active',
        timezone: userData.company_timezone || 'Africa/Brazzaville'
      };

      setToken(newToken);
      setUser(userData);
      setCompany(companyData);

      storeAuthData(newToken, userData, companyData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

      // ✅ FIX — Enregistrer la date de connexion pour le rechargement kiosque
      const today = new Date().toISOString().split('T')[0];
      localStorage.setItem('hp_kiosk_load_date', today);

      console.log('✅ [Auth] Connexion réussie');
      console.log(`👤 [Auth] User: ${userData.email} (${userData.role})`);

      return {
        success: true,
        user: userData,
        token: newToken,
        company: companyData
      };

    } catch (err) {
      console.error('❌ [Auth] Erreur de connexion:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Erreur de connexion';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
        user: null,
        token: null,
        company: null
      };
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================================
  // LOGOUT
  // ==========================================================================
  const logout = () => {
    console.log('🚪 [Auth] Déconnexion...');

    if (user) {
      clearAuthData(user.role);
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('company');
      localStorage.removeItem('superadmin_token');
      localStorage.removeItem('superadmin_user');
      localStorage.removeItem('superadmin_company');
      // ✅ Ne PAS supprimer hp_parametres_pointage et hp_kiosk_load_date
      // ce ne sont pas des données sensibles et leur conservation évite
      // un rechargement inutile au prochain démarrage
    }

    setToken(null);
    setUser(null);
    setCompany(null);
    setError(null);

    delete axios.defaults.headers.common['Authorization'];

    console.log('✅ [Auth] Déconnexion réussie');
  };

  // ==========================================================================
  // HELPERS
  // ==========================================================================
  const isAuthenticated = () => {
    // ✅ FIX — Vérifier aussi l'expiration, pas seulement la présence
    if (!token || !user) return false;
    if (isTokenExpired(token)) {
      // Nettoyer silencieusement si token expiré
      clearAuthData(user?.role || '');
      return false;
    }
    return true;
  };

  const isSuperAdmin = () => {
    return user?.role === 'super_admin';
  };

  // ==========================================================================
  // VALEUR DU CONTEXTE
  // ==========================================================================
  const value = {
    user,
    company,
    token,
    loading,
    error,
    login,
    logout,
    isAuthenticated,
    isSuperAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ============================================================================
// HOOK POUR UTILISER LE CONTEXTE
// ============================================================================
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
